import { useCallback } from 'react';

import {
  getLocalCartUuid,
  useKioskUser,
  useKioskUserLogout,
  useRemoveUserFromCart,
} from '../../../data-access';
import { useResetKiosk } from './useResetKiosk';

/**This hook executes any additional updates that need to occur after a user logs out of the kiosk
 * - logout: logs the user out and removes their userId from the cart (if a cart exists)
 * - logoutAndReset: logs the user out and completely resets the kiosk, returning to the landing page
 *
 * If the user needs to log out without these additional updates happening, the mutation returned by
 * useKioskUserLogout can be used directly.
 */

export const useLogoutUser = () => {
  const { resetKiosk } = useResetKiosk();

  const { data: kioskUser } = useKioskUser();

  const cartUuid = getLocalCartUuid();

  const { mutate: removeUser } = useRemoveUserFromCart();
  const { mutate: logout } = useKioskUserLogout({
    onSuccess: () => {
      cartUuid && removeUser({ uuid: cartUuid });
    },
  });

  const handleLogoutAndReset = useCallback(() => {
    if (kioskUser?.email) {
      logout(undefined, {
        onSettled: () => resetKiosk(),
      });
    } else {
      resetKiosk();
    }
  }, [kioskUser?.email, logout, resetKiosk]);

  return { logout, logoutAndReset: handleLogoutAndReset };
};
